import type { PurchaseOverheadGroup } from '../types/purchasing';
import type { CashFlowProjectionItem } from './cashFlowProjection';
import type { ForecastQuoteCoverageMarker } from './financesQuoteCoverageWarnings';
import { overheadGroupLabel } from './purchasingSpendClass';

export type ForecastDetailGroup = {
  key: string;
  label: string;
  projectId?: string;
  total: number;
  items: CashFlowProjectionItem[];
  quoteMarkers: ForecastQuoteCoverageMarker[];
  /** Approved open POs above this month's budget envelope. */
  budgetOverBy?: number;
};

export function forecastDetailGroupKey(item: Pick<CashFlowProjectionItem, 'projectId' | 'projectName' | 'projectLabel'>): string {
  if (item.projectId) return `id:${item.projectId}`;
  const name = (item.projectName || item.projectLabel || '').trim();
  return name ? `name:${name}` : 'other';
}

export function forecastDetailGroupLabel(item: Pick<CashFlowProjectionItem, 'projectName' | 'projectLabel'>): string {
  return (item.projectName || item.projectLabel || 'Other').trim() || 'Other';
}

export type ForecastMoneyOutSectionKey = 'pay_now' | 'projects' | 'burn_pos_tax' | 'planned' | 'capex';
export type ForecastMoneyOutBucketKey = 'burn' | 'bonus' | 'pos' | 'tax';

/**
 * Every dated planned cash-out counted in `plannedOut` / tooltip Planned:
 * bonus, tax, approved CapEx plans (`scenario` / plannedKind other), and other.
 */
export function isForecastPlannedOut(
  item: Pick<CashFlowProjectionItem, 'kind'>,
): boolean {
  return item.kind === 'planned';
}

/** Year-end bonus / other dated obligations — not a PO and not tax. */
export function isForecastPlannedBonus(
  item: Pick<CashFlowProjectionItem, 'kind' | 'plannedKind'>,
): boolean {
  return item.kind === 'planned' && item.plannedKind !== 'tax';
}

/** Job-linked AP (issued project POs, RFQ awards, quote placeholders). */
export function isForecastProjectPo(
  item: Pick<CashFlowProjectionItem, 'kind' | 'quotePlaceholder' | 'projectId' | 'projectName' | 'dealId' | 'spendClass'>,
): boolean {
  if (item.kind !== 'ap') return false;
  if (item.quotePlaceholder) return true;
  if (item.spendClass === 'job') return true;
  if (item.spendClass === 'overhead' || item.spendClass === 'capex') return false;
  if (item.projectId) return true;
  if ((item.projectName || '').trim()) return true;
  if ((item.dealId || '').trim()) return true;
  return false;
}

/** Company OpEx / overhead POs — no job, deal, or timber project. */
export function isForecastOverheadPo(
  item: Pick<CashFlowProjectionItem, 'kind' | 'quotePlaceholder' | 'projectId' | 'projectName' | 'dealId' | 'spendClass'>,
): boolean {
  if (item.kind !== 'ap' || item.quotePlaceholder) return false;
  if (item.spendClass === 'overhead') return true;
  if (item.spendClass === 'job' || item.spendClass === 'capex') return false;
  return !isForecastProjectPo(item);
}

/**
 * CapEx stays out of job totals. All `kind === 'planned'` rows (bonus, tax,
 * CapEx plans, other) go in Planned — never nested under Burn or Capex — so
 * Money Out, tooltip Planned, and footer plannedOut stay one list.
 * CapEx section is only for CapEx AP / payables (`spendClass === 'capex'`).
 * Pay-now simulations are their own section.
 */
export function forecastMoneyOutSectionKey(
  item: Pick<
    CashFlowProjectionItem,
    'kind' | 'scenario' | 'plannedKind' | 'projectId' | 'projectName' | 'dealId' | 'quotePlaceholder' | 'spendClass' | 'payNowKey'
  >,
): ForecastMoneyOutSectionKey {
  if (item.payNowKey) return 'pay_now';
  if (isForecastPlannedOut(item)) return 'planned';
  if (item.spendClass === 'capex' || (item.scenario === true && item.kind === 'ap')) return 'capex';
  if (isForecastOverheadPo(item)) return 'burn_pos_tax';
  if (item.projectId || (item.projectName || '').trim() || isForecastProjectPo(item)) return 'projects';
  return 'burn_pos_tax';
}

export const MONEY_OUT_FILTER_TAGS = [
  'timber',
  'cnc',
  'bills',
  'estimated',
  'project_costs',
  'pos',
  'capex',
  'burn',
  'tax',
  'bonus',
] as const;

export type MoneyOutFilterTag = (typeof MONEY_OUT_FILTER_TAGS)[number];

export const MONEY_OUT_FILTER_LABELS: Record<MoneyOutFilterTag, string> = {
  timber: 'Timber',
  cnc: 'CNC',
  bills: 'Bills',
  estimated: 'Estimated',
  project_costs: 'Project costs',
  pos: 'POs',
  capex: 'CapEx',
  burn: 'Burn',
  tax: 'Tax',
  bonus: 'Bonus',
};

type MoneyOutTagItem = Pick<
  CashFlowProjectionItem,
  | 'kind'
  | 'scenario'
  | 'plannedKind'
  | 'quotePlaceholder'
  | 'quoteKind'
  | 'estimated'
  | 'spendClass'
  | 'payNowKey'
  | 'requisitionId'
  | 'billId'
  | 'contractorInvoiceId'
  | 'projectId'
  | 'projectName'
  | 'dealId'
>;

/** Tags a money-out row matches. A row can carry more than one. */
export function moneyOutFilterTags(
  item: MoneyOutTagItem,
  billCategory?: string | null,
): MoneyOutFilterTag[] {
  if (item.payNowKey) {
    return moneyOutFilterTags({ ...item, payNowKey: undefined, scenario: false }, billCategory);
  }
  if (item.quotePlaceholder) {
    const tags: MoneyOutFilterTag[] = ['estimated'];
    if (item.quoteKind === 'cnc') tags.push('cnc');
    else if (item.quoteKind === 'timber') tags.push('timber');
    return tags;
  }
  if (item.kind === 'job') {
    const tags: MoneyOutFilterTag[] = ['project_costs'];
    if (item.estimated) tags.push('estimated');
    return tags;
  }
  if (item.plannedKind === 'tax') return ['tax'];
  // CapEx plans are kind=planned + scenario; tag CapEx before the bonus catch-all.
  if (item.scenario === true || item.spendClass === 'capex') return ['capex'];
  if (item.kind === 'planned') return ['bonus'];
  if (item.kind !== 'ap') return [];

  const tags: MoneyOutFilterTag[] = [];
  const isPo = Boolean((item.requisitionId || '').trim()) || isForecastOverheadPo(item);
  if (isPo) tags.push('pos');
  if (billCategory === 'timber') tags.push('timber');
  else if (billCategory === 'cnc') tags.push('cnc');
  else if (!isPo || item.billId || item.contractorInvoiceId) tags.push('bills');
  return tags.length ? tags : ['bills'];
}

export function moneyOutKindFilterActive(selected: readonly MoneyOutFilterTag[]): boolean {
  return selected.length > 0;
}

export function itemMatchesMoneyOutKindFilter(
  item: MoneyOutTagItem,
  selected: readonly MoneyOutFilterTag[],
  billCategory?: string | null,
): boolean {
  if (!moneyOutKindFilterActive(selected)) return true;
  return moneyOutFilterTags(item, billCategory).some((tag) => selected.includes(tag));
}

/** Tags that have something to show in this slice, in menu order. */
export function moneyOutFilterTagsPresent(input: {
  items: MoneyOutTagItem[];
  burn?: number;
  categoryFor?: (item: MoneyOutTagItem) => string | null | undefined;
}): MoneyOutFilterTag[] {
  const present = new Set<MoneyOutFilterTag>();
  if ((input.burn ?? 0) > 0) present.add('burn');
  for (const item of input.items) {
    for (const tag of moneyOutFilterTags(item, input.categoryFor?.(item))) present.add(tag);
  }
  return MONEY_OUT_FILTER_TAGS.filter((tag) => present.has(tag));
}

export function toggleMoneyOutFilterTag(
  selected: readonly MoneyOutFilterTag[],
  tag: MoneyOutFilterTag,
): MoneyOutFilterTag[] {
  return selected.includes(tag)
    ? selected.filter((entry) => entry !== tag)
    : [...selected, tag];
}

/** Narrow the breakdown inputs. An empty selection leaves the slice unchanged. */
export function selectMoneyOutForKindFilter<T extends MoneyOutTagItem>(input: {
  items: T[];
  burn: ForecastMoneyOutBurn | null;
  opexBudgetMonthlyByGroup?: Record<string, number>;
  quoteMarkers?: ForecastQuoteCoverageMarker[];
  selected: readonly MoneyOutFilterTag[];
  categoryFor?: (item: T) => string | null | undefined;
}): {
  items: T[];
  burn: ForecastMoneyOutBurn | null;
  opexBudgetMonthlyByGroup?: Record<string, number>;
  quoteMarkers: ForecastQuoteCoverageMarker[];
} {
  const markers = input.quoteMarkers ?? [];
  if (!moneyOutKindFilterActive(input.selected)) {
    return {
      items: input.items,
      burn: input.burn,
      opexBudgetMonthlyByGroup: input.opexBudgetMonthlyByGroup,
      quoteMarkers: markers,
    };
  }
  const burnOn = input.selected.includes('burn');
  return {
    items: input.items.filter((item) => itemMatchesMoneyOutKindFilter(item, input.selected, input.categoryFor?.(item))),
    burn: burnOn ? input.burn : null,
    opexBudgetMonthlyByGroup: burnOn ? input.opexBudgetMonthlyByGroup : undefined,
    quoteMarkers: input.selected.includes('estimated') ? markers : [],
  };
}

export type ForecastFlowFilterClass = 'project' | 'capex' | 'overhead';

export type ForecastFlowFilter = {
  project: boolean;
  capex: boolean;
  overhead: boolean;
  /** `forecastDetailGroupKey` values. Empty means every job when `project` is on. */
  projectKeys?: string[];
};

export const FORECAST_FLOW_FILTER_OFF: ForecastFlowFilter = {
  project: false,
  capex: false,
  overhead: false,
  projectKeys: [],
};

export type ForecastFlowFilterProject = {
  key: string;
  label: string;
};

export function forecastFlowFilterProjectKeys(filter: ForecastFlowFilter): string[] {
  return filter.projectKeys ?? [];
}

export function forecastFlowFilterIsActive(filter: ForecastFlowFilter): boolean {
  return filter.project || filter.capex || filter.overhead || forecastFlowFilterProjectKeys(filter).length > 0;
}

export function forecastFlowFilterMarkerKey(
  marker: Pick<ForecastQuoteCoverageMarker, 'projectId' | 'projectLabel'>,
): string {
  if (marker.projectId) return `id:${marker.projectId}`;
  const name = (marker.projectLabel || '').trim();
  return name ? `name:${name}` : 'other';
}

export function listForecastFlowFilterProjects(
  items: CashFlowProjectionItem[],
  quoteMarkers: ForecastQuoteCoverageMarker[] = [],
): ForecastFlowFilterProject[] {
  const labels = new Map<string, string>();
  for (const item of items) {
    if (forecastFlowFilterClass(item) !== 'project') continue;
    const key = forecastDetailGroupKey(item);
    if (!labels.has(key)) labels.set(key, forecastDetailGroupLabel(item));
  }
  for (const marker of quoteMarkers) {
    const key = forecastFlowFilterMarkerKey(marker);
    if (!labels.has(key)) labels.set(key, (marker.projectLabel || 'Other').trim() || 'Other');
  }
  return Array.from(labels.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function forecastFlowFilterProjectClassState(
  filter: ForecastFlowFilter,
  allKeys: string[],
): 'on' | 'off' | 'mixed' {
  const keys = forecastFlowFilterProjectKeys(filter);
  if (keys.length === 0) return filter.project ? 'on' : 'off';
  if (allKeys.length > 0 && keys.length === allKeys.length) return 'on';
  return 'mixed';
}

export function forecastFlowFilterProjectKeyChecked(
  filter: ForecastFlowFilter,
  projectKey: string,
): boolean {
  const keys = forecastFlowFilterProjectKeys(filter);
  if (keys.length === 0) return filter.project === true;
  return keys.includes(projectKey);
}

export function toggleForecastFlowFilterClass(
  filter: ForecastFlowFilter,
  key: ForecastFlowFilterClass,
): ForecastFlowFilter {
  if (key !== 'project') {
    return { ...filter, projectKeys: forecastFlowFilterProjectKeys(filter), [key]: !filter[key] };
  }
  const keys = forecastFlowFilterProjectKeys(filter);
  const allOn = filter.project && keys.length === 0;
  if (allOn) return { ...filter, project: false, projectKeys: [] };
  return { ...filter, project: true, projectKeys: [] };
}

export function toggleForecastFlowFilterProjectKey(
  filter: ForecastFlowFilter,
  projectKey: string,
  allKeys: string[],
): ForecastFlowFilter {
  const selected = new Set(forecastFlowFilterProjectKeys(filter));
  const allOn = filter.project && selected.size === 0;
  if (allOn) {
    const next = allKeys.filter((key) => key !== projectKey);
    return { ...filter, project: false, projectKeys: next };
  }
  if (selected.has(projectKey)) selected.delete(projectKey);
  else selected.add(projectKey);
  const projectKeys = Array.from(selected);
  if (allKeys.length > 0 && projectKeys.length === allKeys.length) {
    return { ...filter, project: true, projectKeys: [] };
  }
  return { ...filter, project: false, projectKeys };
}

export function forecastFlowFilterClass(
  item: Pick<
    CashFlowProjectionItem,
    | 'kind'
    | 'scenario'
    | 'plannedKind'
    | 'projectId'
    | 'projectName'
    | 'projectLabel'
    | 'dealId'
    | 'quotePlaceholder'
    | 'spendClass'
    | 'payNowKey'
  >,
): ForecastFlowFilterClass {
  if (item.kind === 'ar') return 'project';
  if (item.payNowKey) {
    return forecastFlowFilterClass({ ...item, payNowKey: undefined, scenario: false });
  }
  // CapEx plans are Planned in the day panel but still Capex in the flow filter.
  if (item.scenario === true || item.spendClass === 'capex') return 'capex';
  if (item.kind === 'planned') return 'overhead';
  const section = forecastMoneyOutSectionKey(item);
  if (section === 'capex') return 'capex';
  if (section === 'projects') return 'project';
  return 'overhead';
}

export function itemVisibleInForecastFlowFilter(
  item: Pick<
    CashFlowProjectionItem,
    | 'kind'
    | 'scenario'
    | 'plannedKind'
    | 'projectId'
    | 'projectName'
    | 'projectLabel'
    | 'dealId'
    | 'quotePlaceholder'
    | 'spendClass'
  >,
  filter: ForecastFlowFilter,
): boolean {
  if (!forecastFlowFilterIsActive(filter)) return true;
  const cls = forecastFlowFilterClass(item);
  if (cls === 'project') {
    const keys = forecastFlowFilterProjectKeys(filter);
    if (keys.length > 0) return keys.includes(forecastDetailGroupKey(item));
    return filter.project === true;
  }
  return filter[cls] === true;
}

export type ForecastFlowFilterSlice = {
  items: CashFlowProjectionItem[];
  quoteMarkers: ForecastQuoteCoverageMarker[];
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
};

/** Visible bar / drill-down totals. Does not change projected cash. */
export function applyForecastFlowFilter(input: {
  items: CashFlowProjectionItem[];
  burn?: number;
  quoteMarkers?: ForecastQuoteCoverageMarker[];
  filter: ForecastFlowFilter;
}): ForecastFlowFilterSlice {
  const active = forecastFlowFilterIsActive(input.filter);
  const items = active
    ? input.items.filter((item) => itemVisibleInForecastFlowFilter(item, input.filter))
    : input.items;
  const quoteMarkers = (input.quoteMarkers ?? []).filter((marker) => {
    if (!active) return true;
    const keys = forecastFlowFilterProjectKeys(input.filter);
    if (keys.length > 0) return keys.includes(forecastFlowFilterMarkerKey(marker));
    return input.filter.project === true;
  });
  const burn = !active || input.filter.overhead ? Math.round(input.burn ?? 0) : 0;
  let arIn = 0;
  let apOut = 0;
  let plannedOut = 0;
  let jobOut = 0;
  for (const item of items) {
    const amount = Math.round(item.amount);
    if (amount <= 0) continue;
    if (item.kind === 'ar') arIn += amount;
    else if (item.kind === 'ap' && !item.withinBurnBudget) apOut += amount;
    else if (item.kind === 'planned') plannedOut += amount;
    else if (item.kind === 'job') jobOut += amount;
  }
  return { items, quoteMarkers, arIn, apOut, plannedOut, jobOut, burn };
}

export type ForecastMoneyOutBurn = {
  amount: number;
  label: string;
  sublabel?: string;
};

export type ForecastMoneyOutBucket = {
  key: ForecastMoneyOutBucketKey;
  label: string;
  total: number;
  sublabel?: string;
  entries: ForecastMoneyOutEntry[];
};

export type ForecastMoneyOutEntry =
  | { kind: 'group'; group: ForecastDetailGroup; amount: number }
  | { kind: 'item'; item: CashFlowProjectionItem; amount: number }
  | { kind: 'burn'; amount: number; label: string; sublabel?: string }
  | { kind: 'bucket'; bucket: ForecastMoneyOutBucket; amount: number };

export type ForecastMoneyOutSection = {
  key: ForecastMoneyOutSectionKey;
  label: string;
  total: number;
  entries: ForecastMoneyOutEntry[];
};

const SECTION_META: Array<{ key: ForecastMoneyOutSectionKey; label: string }> = [
  { key: 'pay_now', label: 'Paying now' },
  { key: 'projects', label: 'Projects' },
  { key: 'burn_pos_tax', label: 'Burn + Issued POs' },
  { key: 'planned', label: 'Planned' },
  { key: 'capex', label: 'Capex' },
];

function entrySortLabel(entry: ForecastMoneyOutEntry): string {
  if (entry.kind === 'group') return entry.group.label;
  if (entry.kind === 'item') return entry.item.projectLabel;
  if (entry.kind === 'burn') return entry.label;
  return entry.bucket.label;
}

function sortEntriesByAmount(entries: ForecastMoneyOutEntry[]): ForecastMoneyOutEntry[] {
  return entries.slice().sort((a, b) => {
    if (b.amount !== a.amount) return b.amount - a.amount;
    return entrySortLabel(a).localeCompare(entrySortLabel(b));
  });
}

function burnBudgetEntries(
  items: CashFlowProjectionItem[],
  budgets: Record<string, number> | undefined,
): ForecastMoneyOutEntry[] {
  const byGroup: Record<string, CashFlowProjectionItem[]> = {};
  for (const item of items) {
    const key = (item.overheadGroup || '').trim() || 'budget';
    const list = byGroup[key] ?? [];
    list.push({
      ...item,
      // Keep the "inside the monthly budget" cue so burn-nested POs read as
      // already covered by OpEx burn (not charged again as AP).
      anchor: item.anchor && /^Inside .+ budget$/.test(item.anchor) && item.anchor !== 'Inside the monthly budget'
        ? undefined
        : item.anchor,
    });
    byGroup[key] = list;
  }
  const keys: Record<string, true> = {};
  for (const key of Object.keys(byGroup)) keys[key] = true;
  if (budgets) {
    for (const key of Object.keys(budgets)) keys[key] = true;
  }
  const entries: ForecastMoneyOutEntry[] = [];
  for (const key of Object.keys(keys)) {
    const groupItems = (byGroup[key] ?? [])
      .slice()
      .sort((a, b) => b.amount - a.amount || a.projectLabel.localeCompare(b.projectLabel));
    const poSum = groupItems.reduce((sum, item) => sum + item.amount, 0);
    const envelope = budgets && Object.prototype.hasOwnProperty.call(budgets, key)
      ? Number(budgets[key])
      : null;
    const total = envelope != null && Number.isFinite(envelope)
      ? Math.round(Math.max(0, envelope, poSum))
      : poSum;
    const overBy = envelope != null && Number.isFinite(envelope)
      ? Math.round(poSum - envelope)
      : 0;
    if (total <= 0 && groupItems.length === 0) continue;
    const label = key === 'budget'
      ? 'Budget'
      : (overheadGroupLabel(key as PurchaseOverheadGroup) || key);
    const group: ForecastDetailGroup = {
      key: `opex:${key}`,
      label,
      total,
      items: groupItems,
      quoteMarkers: [],
      budgetOverBy: overBy > 0 ? overBy : undefined,
    };
    entries.push({ kind: 'group', group, amount: total });
  }
  return sortEntriesByAmount(entries);
}

/** Payroll and recurring overhead are in the Burn total but not in an OpEx group. */
function withUnlistedBurn(
  entries: ForecastMoneyOutEntry[],
  burnAmount: number,
): ForecastMoneyOutEntry[] {
  if (entries.length === 0) return entries;
  const listed = entries.reduce((sum, entry) => sum + entry.amount, 0);
  const rest = Math.round(burnAmount - listed);
  if (rest <= 0) return entries;
  return sortEntriesByAmount([
    ...entries,
    {
      kind: 'burn',
      amount: rest,
      label: 'Payroll + overhead',
      sublabel: 'Loaded payroll and recurring overhead',
    },
  ]);
}

function toItemEntries(items: CashFlowProjectionItem[]): ForecastMoneyOutEntry[] {
  return sortEntriesByAmount(
    items.map((item) => ({
      kind: 'item' as const,
      item,
      amount: item.amount,
    })),
  );
}

function companyBucket(
  key: ForecastMoneyOutBucketKey,
  label: string,
  entries: ForecastMoneyOutEntry[],
  extra?: { total?: number; sublabel?: string },
): ForecastMoneyOutEntry {
  const total = extra?.total ?? entries.reduce((sum, entry) => sum + entry.amount, 0);
  return {
    kind: 'bucket',
    amount: total,
    bucket: {
      key,
      label,
      total,
      sublabel: extra?.sublabel,
      entries,
    },
  };
}

export function buildForecastMoneyOutSections(input: {
  items: CashFlowProjectionItem[];
  quoteMarkers?: ForecastQuoteCoverageMarker[];
  burn?: ForecastMoneyOutBurn | null;
  /** Monthly equivalent of each OpEx group that has a budget set. */
  opexBudgetMonthlyByGroup?: Record<string, number>;
}): ForecastMoneyOutSection[] {
  const quoteMarkers = input.quoteMarkers || [];
  const buckets: Record<ForecastMoneyOutSectionKey, CashFlowProjectionItem[]> = {
    pay_now: [],
    projects: [],
    burn_pos_tax: [],
    planned: [],
    capex: [],
  };
  for (const item of input.items) {
    buckets[forecastMoneyOutSectionKey(item)].push(item);
  }

  const projectEntries: ForecastMoneyOutEntry[] = groupForecastItemsByProject(
    buckets.projects,
    quoteMarkers,
  ).map((group) => ({
    kind: 'group' as const,
    group,
    amount: group.total,
  }));

  const burn = input.burn && input.burn.amount > 0 ? input.burn : null;
  const burnBudgetPos = buckets.burn_pos_tax.filter((item) => item.withinBurnBudget);
  const poItems = buckets.burn_pos_tax.filter(
    (item) => isForecastOverheadPo(item) && !item.withinBurnBudget,
  );
  const otherCompany = buckets.burn_pos_tax.filter(
    (item) => !isForecastOverheadPo(item),
  );
  const poEntries: ForecastMoneyOutEntry[] = [
    ...groupForecastItemsByProject(poItems).map((group) => ({
      kind: 'group' as const,
      group,
      amount: group.total,
    })),
    ...toItemEntries(otherCompany),
  ];
  const burnGroups = burnBudgetEntries(burnBudgetPos, input.opexBudgetMonthlyByGroup);
  const companyEntries: ForecastMoneyOutEntry[] = [
    companyBucket(
      'burn',
      'Burn',
      burn ? withUnlistedBurn(burnGroups, burn.amount) : burnGroups,
      {
        total: burn ? burn.amount : undefined,
        sublabel: burn?.sublabel || (burn ? burn.label : undefined),
      },
    ),
    companyBucket('pos', 'POs', poEntries),
  ];

  const plannedEntries = toItemEntries(buckets.planned);
  const capexEntries = toItemEntries(buckets.capex);
  const payNowEntries = toItemEntries(buckets.pay_now);

  const byKey: Record<ForecastMoneyOutSectionKey, ForecastMoneyOutEntry[]> = {
    pay_now: payNowEntries,
    projects: sortEntriesByAmount(projectEntries),
    burn_pos_tax: companyEntries,
    planned: plannedEntries,
    capex: capexEntries,
  };

  return SECTION_META
    .map((meta) => {
      const entries = byKey[meta.key];
      const total = entries.reduce((sum, entry) => sum + entry.amount, 0);
      return { ...meta, total, entries };
    })
    .filter((section) => {
      if (section.key === 'burn_pos_tax') return section.total > 0;
      return section.entries.length > 0;
    });
}

export type ForecastMoneyOutFlatRow =
  | {
      kind: 'item';
      item: CashFlowProjectionItem;
      amount: number;
      /** Group heading this row was nested under. */
      groupLabel: string;
      sectionKey: ForecastMoneyOutSectionKey;
    }
  | {
      kind: 'burn';
      amount: number;
      label: string;
      sublabel?: string;
      groupLabel: string;
      sectionKey: ForecastMoneyOutSectionKey;
    }
  | {
      kind: 'envelope';
      amount: number;
      label: string;
      sublabel?: string;
      groupLabel: string;
      sectionKey: ForecastMoneyOutSectionKey;
    }
  | {
      kind: 'quote';
      marker: ForecastQuoteCoverageMarker;
      groupLabel: string;
      sectionKey: ForecastMoneyOutSectionKey;
    };

function pushFlatEntry(
  entry: ForecastMoneyOutEntry,
  groupLabel: string,
  sectionKey: ForecastMoneyOutSectionKey,
  rows: ForecastMoneyOutFlatRow[],
): void {
  if (entry.kind === 'item') {
    rows.push({ kind: 'item', item: entry.item, amount: entry.amount, groupLabel, sectionKey });
    return;
  }
  if (entry.kind === 'burn') {
    rows.push({
      kind: 'burn',
      amount: entry.amount,
      label: entry.label,
      sublabel: entry.sublabel,
      groupLabel,
      sectionKey,
    });
    return;
  }
  if (entry.kind === 'group') {
    const itemSum = entry.group.items.reduce((sum, item) => sum + item.amount, 0);
    for (const item of entry.group.items) {
      rows.push({
        kind: 'item',
        item,
        amount: item.amount,
        groupLabel: entry.group.label,
        sectionKey,
      });
    }
    for (const marker of entry.group.quoteMarkers) {
      rows.push({ kind: 'quote', marker, groupLabel: entry.group.label, sectionKey });
    }
    const rest = Math.round(entry.group.total - itemSum);
    if (rest > 0) {
      rows.push({
        kind: 'envelope',
        amount: rest,
        label: entry.group.label,
        sublabel: 'Budget not on a purchase yet',
        groupLabel: entry.group.label,
        sectionKey,
      });
    }
    return;
  }
  for (const child of entry.bucket.entries) {
    const childLabel = child.kind === 'group' ? child.group.label : entry.bucket.label;
    pushFlatEntry(child, childLabel, sectionKey, rows);
  }
  const childSum = entry.bucket.entries.reduce((sum, child) => sum + child.amount, 0);
  const rest = Math.round(entry.bucket.total - childSum);
  if (rest > 0) {
    rows.push({
      kind: 'envelope',
      amount: rest,
      label: entry.bucket.label,
      sublabel: entry.bucket.sublabel,
      groupLabel: entry.bucket.label,
      sectionKey,
    });
  }
}

export type MoneyOutFlatSummaryLine = {
  label: string;
  amount: number;
};

function moneyOutFlatKindLabel(
  row: ForecastMoneyOutFlatRow,
  billCategory?: string | null,
): string | null {
  if (row.kind === 'quote') return null;
  if (row.kind === 'burn') return 'Payroll + overhead';
  if (row.kind === 'envelope') return row.label;
  const item = row.item;
  if (item.kind === 'job') return item.label.trim() || 'Project costs';
  if (item.quotePlaceholder) return item.quoteKind === 'cnc' ? 'CNC' : 'Timber';
  if (item.plannedKind === 'tax') return 'Tax';
  if (item.scenario === true || item.spendClass === 'capex') return 'CapEx';
  if (item.kind === 'planned') return item.plannedKind === 'bonus' ? 'Bonus' : 'Planned';
  if (billCategory === 'timber') return 'Timber';
  if (billCategory === 'cnc') return 'CNC';
  const tag = moneyOutFilterTags(item, billCategory)[0];
  return tag ? MONEY_OUT_FILTER_LABELS[tag] : row.groupLabel;
}

function summaryLinesFromMap(totals: Map<string, number>): MoneyOutFlatSummaryLine[] {
  return Array.from(totals.entries())
    .map(([label, amount]) => ({ label, amount: Math.round(amount) }))
    .filter((line) => line.amount > 0 && line.label.trim())
    .sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label));
}

/**
 * Totals for the filtered flat list. Splits by kind (Materials, Timber, …).
 * A single kind falls back to the project or group totals.
 */
export function summarizeMoneyOutFlatRows(
  rows: ForecastMoneyOutFlatRow[],
  categoryFor?: (item: CashFlowProjectionItem) => string | null | undefined,
): MoneyOutFlatSummaryLine[] {
  const byKind = new Map<string, number>();
  for (const row of rows) {
    if (row.kind === 'quote') continue;
    const label = moneyOutFlatKindLabel(
      row,
      row.kind === 'item' ? categoryFor?.(row.item) : undefined,
    );
    if (!label) continue;
    byKind.set(label, (byKind.get(label) ?? 0) + row.amount);
  }
  const byKindLines = summaryLinesFromMap(byKind);
  if (byKindLines.length > 1) return byKindLines;

  const byGroup = new Map<string, number>();
  for (const row of rows) {
    if (row.kind === 'quote') continue;
    const label = row.groupLabel.trim();
    if (!label) continue;
    byGroup.set(label, (byGroup.get(label) ?? 0) + row.amount);
  }
  const byGroupLines = summaryLinesFromMap(byGroup);
  return byGroupLines.length > 1 ? byGroupLines : byKindLines;
}

/** Leaf rows in the same order as the expanded sections, without the headings. */
export function flattenForecastMoneyOutSections(
  sections: ForecastMoneyOutSection[],
): ForecastMoneyOutFlatRow[] {
  const rows: ForecastMoneyOutFlatRow[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      const label = entry.kind === 'group'
        ? entry.group.label
        : entry.kind === 'bucket'
          ? entry.bucket.label
          : entry.kind === 'burn'
            ? entry.label
            : section.label;
      pushFlatEntry(entry, label, section.key, rows);
    }
  }
  return rows;
}

/** Flat list in section order — tests and older callers. */
export function buildForecastMoneyOutEntries(
  items: CashFlowProjectionItem[],
  quoteMarkers: ForecastQuoteCoverageMarker[] = [],
  burn?: ForecastMoneyOutBurn | null,
): ForecastMoneyOutEntry[] {
  return buildForecastMoneyOutSections({ items, quoteMarkers, burn }).flatMap((section) => (
    section.entries.flatMap((entry) => (entry.kind === 'bucket' ? [entry, ...entry.bucket.entries] : [entry]))
  ));
}

export function groupForecastItemsByProject(
  items: CashFlowProjectionItem[],
  quoteMarkers: ForecastQuoteCoverageMarker[] = [],
): ForecastDetailGroup[] {
  const groups = new Map<string, ForecastDetailGroup>();

  const ensure = (
    key: string,
    label: string,
    projectId?: string,
  ): ForecastDetailGroup => {
    const existing = groups.get(key);
    if (existing) return existing;
    const created: ForecastDetailGroup = {
      key,
      label,
      projectId,
      total: 0,
      items: [],
      quoteMarkers: [],
    };
    groups.set(key, created);
    return created;
  };

  for (const item of items) {
    const group = ensure(
      forecastDetailGroupKey(item),
      forecastDetailGroupLabel(item),
      item.projectId,
    );
    group.items.push(item);
    group.total += item.amount;
  }

  for (const marker of quoteMarkers) {
    const key = marker.projectId ? `id:${marker.projectId}` : `name:${marker.projectLabel}`;
    const group = ensure(key, marker.projectLabel, marker.projectId);
    group.quoteMarkers.push(marker);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.label.localeCompare(b.label);
  });
}
